import { TAsset } from "types";
import { TProximitySensorReport } from "types/features";

// --- Tunable constants (all deliberately named so they can be adjusted in one place) ---
/** How often the live check polls the device while running. */
export const POLL_INTERVAL_MS = 1000;
/** Readings used for the primary status rolling average. */
export const ROLLING_WINDOW = 5;
/** Readings used for the secondary (steadier) average shown in the detail view. */
export const LONG_WINDOW = 20;
/** Max readings retained per sensor (drives the detail chart and long average). */
export const HISTORY_LIMIT = 60;
/** Rolling average at or below this counts as "no signal" (reads ~0) for a linked sensor. */
export const NO_SIGNAL_MAX = 1;
/** Consecutive polls a previously-seen board may miss before it is "not responding". */
export const BOARD_MISS_LIMIT = 3;
/** Readings examined when detecting flicker. */
export const FLICKER_WINDOW = 8;
/** Threshold crossings within FLICKER_WINDOW to warn the sensor is unstable. */
export const FLICKER_MIN_CROSSINGS = 3;

export type SensorKey = string; // `${boardId}:${position}`

export type TriageBucket = "working" | "needs-look" | "not-responding" | "unlinked";

export type SensorStatus =
  | "detecting"
  | "nothing-detected"
  | "no-signal"
  | "flickering"
  | "not-responding"
  | "unlinked";

export type BoardSession = {
  boardId: number;
  lastSeenPoll: number;
  sensorPositions: number[];
};

export type SensorSession = {
  sensors: Record<SensorKey, number[]>;
  boards: Record<number, BoardSession>;
  pollCount: number;
};

export type SensorLinkMap = Record<SensorKey, TAsset>;

export type SensorView = {
  key: SensorKey;
  boardId: number;
  position: number;
  latest: number;
  rollingAvg: number;
  longAvg: number;
  history: number[];
  linkedAsset?: TAsset;
  threshold: number | null;
  status: SensorStatus;
  bucket: TriageBucket;
  flickering: boolean;
  boardResponding: boolean;
};

export type BoardView = {
  boardId: number;
  responding: boolean;
  sensors: SensorView[];
};

export type SensorHealthView = {
  boards: BoardView[];
  counts: Record<TriageBucket, number>;
  totalSensors: number;
};

/** The single at-a-glance verdict shown in the page's overview band. */
export type OverallTone = "good" | "warn" | "bad" | "neutral";

export type OverallStatus = {
  tone: OverallTone;
  headline: string;
  detail: string;
};

export const TRIAGE_LABELS: Record<TriageBucket, string> = {
  working: "Working normally",
  "needs-look": "Needs a look",
  "not-responding": "Not responding",
  unlinked: "Not yet linked",
};

export const STATUS_LABELS: Record<SensorStatus, string> = {
  detecting: "Detecting",
  "nothing-detected": "Nothing detected",
  "no-signal": "No signal",
  flickering: "Unstable reading",
  "not-responding": "Board not responding",
  unlinked: "Not yet linked to a tool",
};

export const STATUS_EXPLANATIONS: Record<SensorStatus, string> = {
  detecting: "A tool is in front of this sensor — the reading is at or above its detection threshold.",
  "nothing-detected":
    "The sensor is healthy and reporting, but nothing is in front of it right now (the tool has likely been taken).",
  "no-signal":
    "This sensor keeps reading close to zero. That can mean a genuinely missing tool or a sensor that needs checking.",
  flickering:
    "This reading keeps flipping between detecting and not detecting — the sensor may be loose or faulty.",
  "not-responding":
    "This sensor's board has stopped reporting. The values shown are the last ones received before it went quiet.",
  unlinked:
    "This sensor isn't linked to a tool yet. It's reporting normally — linking happens during calibration setup.",
};

export const sensorKey = (boardId: number, position: number): SensorKey => `${boardId}:${position}`;

export const emptySession = (): SensorSession => ({ sensors: {}, boards: {}, pollCount: 0 });

export const average = (values: number[], window: number): number => {
  if (values.length === 0) return 0;
  const slice = values.slice(-window);
  return slice.reduce((sum, value) => sum + value, 0) / slice.length;
};

export const countThresholdCrossings = (values: number[], threshold: number, window: number): number => {
  const slice = values.slice(-window);
  let crossings = 0;
  for (let i = 1; i < slice.length; i++) {
    if ((slice[i - 1] >= threshold) !== (slice[i] >= threshold)) crossings++;
  }
  return crossings;
};

/** Immutably fold one poll's board reports into the running session. */
export const applyPoll = (session: SensorSession, reports: TProximitySensorReport[]): SensorSession => {
  const pollIndex = session.pollCount;
  const sensors: Record<SensorKey, number[]> = { ...session.sensors };
  const boards: Record<number, BoardSession> = { ...session.boards };

  reports.forEach((report) => {
    const positions = new Set(boards[report.boardId]?.sensorPositions ?? []);
    report.values.forEach(({ position, value }) => {
      const key = sensorKey(report.boardId, position);
      const next = [...(sensors[key] ?? []), value];
      sensors[key] = next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
      positions.add(position);
    });
    boards[report.boardId] = {
      boardId: report.boardId,
      lastSeenPoll: pollIndex,
      sensorPositions: Array.from(positions),
    };
  });

  return { sensors, boards, pollCount: session.pollCount + 1 };
};

export const buildSensorLinkMap = (assets: TAsset[]): SensorLinkMap => {
  const map: SensorLinkMap = {};
  assets.forEach((asset) => {
    const cfg = asset.storageConfig;
    if (!cfg) return;
    if (cfg.irSensorBoardId == null || cfg.irSensorNumber == null) return;
    map[sensorKey(cfg.irSensorBoardId, cfg.irSensorNumber)] = asset;
  });
  return map;
};

export const isBoardResponding = (session: SensorSession, boardId: number): boolean => {
  const board = session.boards[boardId];
  if (!board) return false;
  const latestPollIndex = session.pollCount - 1;
  return latestPollIndex - board.lastSeenPoll < BOARD_MISS_LIMIT;
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Collapses the whole toolbox into one triage verdict, worst-state-first, so the page can lead with
 * the single thing a technician came to learn: is everything OK, or does something need attention?
 */
export const deriveOverallStatus = (view: SensorHealthView, boardCount: number): OverallStatus => {
  const { counts, totalSensors } = view;

  if (totalSensors === 0) {
    return {
      tone: "neutral",
      headline: "Waiting for boards",
      detail: "No sensors have reported yet. They appear here the moment a board checks in.",
    };
  }

  const boards = plural(boardCount, "board");

  if (counts["not-responding"] > 0) {
    return {
      tone: "bad",
      headline: `${plural(counts["not-responding"], "sensor")} not responding`,
      detail: "Their board stopped reporting — the readings shown are the last ones received.",
    };
  }

  if (counts["needs-look"] > 0) {
    return {
      tone: "warn",
      headline: `${plural(counts["needs-look"], "sensor")} need${counts["needs-look"] === 1 ? "s" : ""} a look`,
      detail: `Everything else is reporting normally across ${boards}.`,
    };
  }

  if (counts.working > 0) {
    return {
      tone: "good",
      headline: "All sensors healthy",
      detail:
        counts.unlinked > 0
          ? `${plural(totalSensors, "sensor")} reporting across ${boards} · ${plural(counts.unlinked, "sensor")} not yet linked to a tool.`
          : `${plural(totalSensors, "sensor")} reporting across ${boards}.`,
    };
  }

  return {
    tone: "neutral",
    headline: "No tools linked yet",
    detail: `${plural(totalSensors, "sensor")} reporting across ${boards}. Link tools during calibration to turn on presence detection.`,
  };
};

export const assetLabel = (asset?: TAsset): string =>
  asset?.catalogProduct?.name || asset?.description || asset?.serialNumber || "Unnamed tool";

export const deriveSensorView = (
  boardId: number,
  position: number,
  history: number[],
  linkedAsset: TAsset | undefined,
  boardResponding: boolean,
): SensorView => {
  const latest = history.length ? history[history.length - 1] : 0;
  const rollingAvg = average(history, ROLLING_WINDOW);
  const longAvg = average(history, LONG_WINDOW);
  const threshold = linkedAsset?.storageConfig?.irSensorThreshold ?? null;
  const flickering =
    threshold != null && countThresholdCrossings(history, threshold, FLICKER_WINDOW) >= FLICKER_MIN_CROSSINGS;

  let status: SensorStatus;
  let bucket: TriageBucket;

  if (!boardResponding) {
    status = "not-responding";
    bucket = "not-responding";
  } else if (!linkedAsset) {
    status = "unlinked";
    bucket = "unlinked";
  } else if (rollingAvg <= NO_SIGNAL_MAX) {
    status = "no-signal";
    bucket = "needs-look";
  } else if (flickering) {
    status = "flickering";
    bucket = "needs-look";
  } else if (threshold != null && rollingAvg >= threshold) {
    status = "detecting";
    bucket = "working";
  } else {
    status = "nothing-detected";
    bucket = "working";
  }

  return {
    key: sensorKey(boardId, position),
    boardId,
    position,
    latest,
    rollingAvg,
    longAvg,
    history,
    linkedAsset,
    threshold,
    status,
    bucket,
    flickering,
    boardResponding,
  };
};

/**
 * Shared upper bound for plotting a reading against its threshold, so the cell, gauge and trend chart
 * all agree on where "detecting" sits. Driven by observed data (not the raw 0–255 byte range) with a
 * floor so a quiet sensor still has a sensible axis.
 */
export const sensorScaleMax = (sensor: Pick<SensorView, "history" | "threshold" | "latest">): number => {
  const observedMax = sensor.history.length ? Math.max(...sensor.history) : 0;
  return Math.max(observedMax, sensor.threshold ?? 0, sensor.latest, 32) * 1.1;
};

export const deriveSensorHealthView = (session: SensorSession, linkMap: SensorLinkMap): SensorHealthView => {
  const boards: BoardView[] = Object.values(session.boards)
    .sort((a, b) => a.boardId - b.boardId)
    .map((board) => {
      const responding = isBoardResponding(session, board.boardId);
      const sensors = board.sensorPositions
        .slice()
        .sort((a, b) => a - b)
        .map((position) => {
          const key = sensorKey(board.boardId, position);
          return deriveSensorView(board.boardId, position, session.sensors[key] ?? [], linkMap[key], responding);
        });
      return { boardId: board.boardId, responding, sensors };
    });

  const counts: Record<TriageBucket, number> = { working: 0, "needs-look": 0, "not-responding": 0, unlinked: 0 };
  let totalSensors = 0;
  boards.forEach((board) =>
    board.sensors.forEach((sensor) => {
      counts[sensor.bucket] += 1;
      totalSensors += 1;
    }),
  );

  return { boards, counts, totalSensors };
};
